import { FC, Fragment, useContext, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { FullSegment, MyOrder, OrderRow } from 'ts/shared/interfaces/MOResponse';
import { changeRequest } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import { faSliders } from '@fortawesome/free-solid-svg-icons';
import MoSpecialRequestModal from './MoSpecialRequestModal';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';

interface MoChangeRequestProps {
  segment: FullSegment;
  orderRow: OrderRow;
  onSuccess: (order: MyOrder) => void;
}

const MoChangeRequest: FC<MoChangeRequestProps> = ({ segment, orderRow, ...props }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === segment.orderSegId ||
    activeOrder?.orderRow.segmentId === orderRow.segmentId;
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [specialRequestList, setSpecialRequestList] = useState<any[]>([]);

  const onClickPnrDocket = async (message: string) => {
    setIsLoading(true);
    setOpenModal(false);

    const res = await changeRequest({
      orderId: orderRow.orderId,
      segmentId: segment.orderSegId,
      productType: segment.productType,
      requestMessage: message,
    });

    if (res && !res?.hasErrors) {
      showSuccess(t('mo.change_request_success'));
      props.onSuccess(res.myOrder);
    } else if (res?.hasErrors) {
      showError(res.errorMessage);
    }

    setIsLoading(false);
  };

  useOrdersAction(
    (action) =>
      action.action === 'special-request' &&
      actionTargetsOrder(
        action,
        {
          segmentId: orderRow.segmentId,
          altSegmentId: segment.orderSegId,
          orderId: orderRow.orderId,
          altOrderId: segment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (action.message) {
        void onClickPnrDocket(action.message);
        return;
      }
      setOpenModal(true);
    },
    isActive,
  );

  return (
    <Fragment>
      <Button
        disabled={isLoading}
        onClick={() => setOpenModal(true)}
        title={t(isLoading ? 'mo.loading' : 'mo.change_request')}
        icon={faSliders}
        iconclassname="mr-5"
        className="btn btn-primary btn-ssm"
      />

      {openModal && createPortal(
        <MoSpecialRequestModal
          segment={segment}
          openModal={openModal}
          setOpenModal={(val) => { setOpenModal(val); setSpecialRequestList([]); }}
          specialRequestList={specialRequestList}
          setSpecialRequestList={setSpecialRequestList}
          onSubmit={onClickPnrDocket}
        />
        , document.body)}
    </Fragment>
  );
};

export default withErrorBoundary(MoChangeRequest, 'MoChangeRequest');
